import { useSubscription } from '../hooks/useSubscription'
import { useAuth } from '../hooks/useAuth'
import { CompanyMeasurementProvider } from '../context/CompanyMeasurementProvider'
import { Measure, Dimensions } from '../components/Measurement'
import { useEffect, useLayoutEffect, useId, useRef, useState } from 'react'
import { useMeasurementDrafts } from '../hooks/useMeasurementDrafts'
import { useUnsavedChangesWarning } from '../hooks/useUnsavedChangesWarning'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { vehicleService, Vehicle } from '../services/vehicle.service'
import { VehicleConfigRail } from '../components/optimization/vehicle-visualizer/VehicleConfigRail'
import { VehicleConfigurationProgress, type VehicleCompletion } from '../components/optimization/vehicle-visualizer/VehicleConfigurationProgress'
import StarRating from '../components/ui/StarRating'
import VehicleLibraryDialog from '../components/VehicleLibraryDialog'
import VehicleActionDialog from '../components/VehicleActionDialog'
import VehicleProfileDialog from '../components/VehicleProfileDialog'
import { duplicateReference, duplicateVehiclePayload, vehicleFormFingerprint } from '../lib/vehicleUx'
import { collectAllVehicles } from '../lib/vehicleTransfer'
import {
  TruckIcon,
  WrenchScrewdriverIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
  XMarkIcon,
  CubeIcon,
  MagnifyingGlassIcon,
  ChevronRightIcon,
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  SparklesIcon,
  ChevronLeftIcon,
  DocumentDuplicateIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline'
import { AlertTriangle, Thermometer,Weight,SquareDimensions } from 'lucide-react'
import { type VehicleEquipmentPreset } from '../lib/vehicleEquipmentPresets'
import { deducePresetFromVehicle } from '../lib/vehicleEquipmentHelper'
import { applyPresetToForm } from '../lib/vehiclePresetForm'
import { getVehicleTypeLabel, getVehicleSubtypeLabel, getVehicleDisplayName, vehicleSearchAliases } from '../lib/vehicleHelpers'
import { settingsService } from '../services/settings.service'
import { isFleetTractor, optionalVehicleNumber, prepareVehicleFormPayload, vehicleInteriorVolume } from '../lib/vehicleFormContract'

type RowOperation = { kind: 'delete' | 'duplicate'; vehicle: Vehicle }
type RowOperationError = RowOperation & { message: string }

function vehicleErrorMessage(error: unknown): string {
  const failure = error as { response?: { status?: number; data?: { message?: string } }; status?: number; message?: string }
  const status = failure?.response?.status ?? failure?.status
  if (status === 403) return 'Vous n’avez pas l’autorisation nécessaire pour cette opération. Contactez un administrateur si besoin.'
  if (status === 401) return 'Votre session a expiré. Reconnectez-vous, puis réessayez.'
  const candidate = failure?.response?.data?.message || failure?.message
  const message = typeof candidate === 'string' ? candidate.trim() : ''
  if (['Network Error', 'Failed to fetch'].includes(message || '')) return 'Connexion au serveur impossible. Vérifiez votre connexion, puis réessayez.'
  return message || 'Une erreur est survenue. Veuillez réessayer.'
}

export default function VehicleSettings() {
  return <CompanyMeasurementProvider><VehicleSettingsContent /></CompanyMeasurementProvider>
}

function VehicleSettingsContent() {
  const { canWrite, canRead, isLoading: accessLoading } = useSubscription()
  const { user, isLoading: authLoading } = useAuth()
  const allowed = (permission: string) => user?.permissions?.includes(permission) === true || user?.is_super_admin === true
  const canConsult = canRead && allowed('vehicle:read')
  const canCreate = canConsult && canWrite && allowed('vehicle:create')
  const canUpdate = canConsult && canWrite && allowed('vehicle:update')
  const canDelete = canConsult && canWrite && allowed('vehicle:delete')
  const [viewOnly, setViewOnly] = useState(false)
  const [lastCopy, setLastCopy] = useState<Vehicle | null>(null)
  const [isEditorOpen, setIsEditorOpen] = useState(false)
  const [editingVehicle, setEditingVehicle] = useState<Vehicle | null>(null)
  const [libraryMode, setLibraryMode] = useState<'import' | 'export' | 'presets' | null>(null)
  const [initialPreset, setInitialPreset] = useState<VehicleEquipmentPreset | null>(null)
  const [savedMessage, setSavedMessage] = useState('')
  const [rowOperation, setRowOperation] = useState<RowOperation | null>(null)
  const [rowError, setRowError] = useState<RowOperationError | null>(null)
  // Refs also guard two events dispatched before React has rendered a disabled button.
  const rowOperationRef = useRef<RowOperation | null>(null)
  const saveLock = useRef(false)
  const pageHeadingRef = useRef<HTMLHeadingElement>(null)
  const modelsTriggerRef = useRef<HTMLButtonElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const editorOrigin = useRef<{ trigger: HTMLElement | null; scroller: Element; scrollTop: number } | null>(null)
  const openEditor = (trigger = document.activeElement as HTMLElement | null) => {
    const scroller = listRef.current?.closest('main') ?? document.scrollingElement ?? document.documentElement
    editorOrigin.current = { trigger, scroller, scrollTop: scroller.scrollTop }
    scroller.scrollTop = 0
    setIsEditorOpen(true)
  }
  useLayoutEffect(() => {
    if (isEditorOpen || !editorOrigin.current) return
    const { trigger, scroller, scrollTop } = editorOrigin.current
    editorOrigin.current = null
    // The list stays mounted so its filters, pagination and keyboard origin survive editing.
    ;(trigger?.isConnected && !trigger.matches(':disabled') ? trigger : pageHeadingRef.current)?.focus({ preventScroll: true })
    scroller.scrollTop = scrollTop
  }, [isEditorOpen])
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterStatus, setFilterStatus] = useState<string>('all')
  const [selectedAttributes, setSelectedAttributes] = useState<string[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  useEffect(() => {
    if (searchTerm.trim() === searchQuery) return
    const timer = window.setTimeout(() => { setSearchQuery(searchTerm.trim()); setCurrentPage(1) }, 250)
    return () => window.clearTimeout(timer)
  }, [searchTerm, searchQuery])
  const queryClient = useQueryClient()
  const { data: companySettings } = useQuery({ queryKey: ['settings'], queryFn: settingsService.getSettings })
  const registrationRequired = (companySettings?.tenantConfig.vehicle_registration_required ?? companySettings?.defaults.vehicle_registration_required ?? false) === true

  const toggleAttribute = (attrId: string) => {
    setCurrentPage(1)
    setSelectedAttributes(prev =>
      prev.includes(attrId) ? prev.filter(a => a !== attrId) : [...prev, attrId]
    )
  }

  const { data: loadedVehicles, isInitialLoading: isLoading, isFetching, isError, error: listError, isPreviousData, refetch } = useQuery({
    queryKey: ['vehicles', currentPage, pageSize, searchQuery, filterStatus, selectedAttributes],
    queryFn: async () => vehicleService.getVehicles({ page: currentPage, pageSize, search: searchQuery, ...vehicleSearchAliases(searchQuery), status: filterStatus === 'all' || filterStatus === 'favorites' ? undefined : filterStatus, favorites: filterStatus === 'favorites', reefer: selectedAttributes.includes('reefer'), adr: selectedAttributes.includes('adr') }),
    enabled: canConsult,
    keepPreviousData: true,
    staleTime: 30_000,
    retry: false
  })
  const vehiclesData = canConsult ? loadedVehicles : undefined
  const { data: loadedSummary, isFetching: summaryFetching, isError: summaryError, refetch: refetchSummary } = useQuery({
    queryKey: ['vehicles', 'summary'],
    queryFn: vehicleService.getFleetSummary,
    enabled: canConsult,
    staleTime: 30_000,
    retry: false
  })
  const fleetSummary = canConsult && !summaryError ? loadedSummary : undefined

  const createMutation = useMutation({
    retry: false,
    mutationFn: async (vehicle: Omit<Vehicle, 'id' | 'tenant_id' | 'created_at' | 'updated_at'>) => {
      return vehicleService.createVehicle(vehicle)
    },
    onSuccess: async (created) => {
      setIsEditorOpen(false)
      setEditingVehicle(null)
      setSavedMessage(`Véhicule « ${getVehicleDisplayName(created)} » créé.`)
      await queryClient.invalidateQueries({ queryKey: ['vehicles'] })
    },
    onSettled: () => { saveLock.current = false }
  })

  const updateMutation = useMutation({
    retry: false,
    mutationFn: async ({ id, vehicle }: { id: string; vehicle: Partial<Vehicle> }) => {
      return vehicleService.updateVehicle(id, vehicle)
    },
    onSuccess: async (updated) => {
      setIsEditorOpen(false)
      setEditingVehicle(null)
      setSavedMessage(`Véhicule « ${getVehicleDisplayName(updated)} » enregistré.`)
      await queryClient.invalidateQueries({ queryKey: ['vehicles'] })
    },
    onSettled: () => { saveLock.current = false }
  })

  const finishRowOperation = () => {
    rowOperationRef.current = null
    setRowOperation(null)
  }

  const deleteMutation = useMutation({
    retry: false,
    mutationFn: async (vehicle: Vehicle) => {
      await vehicleService.deleteVehicle(vehicle.id!)
    },
    onSuccess: async (_, vehicle) => {
      setSavedMessage(`Véhicule « ${getVehicleDisplayName(vehicle)} » supprimé.`)
      await queryClient.invalidateQueries({ queryKey: ['vehicles'] })
    },
    onError: (error, vehicle) => setRowError({ kind: 'delete', vehicle, message: vehicleErrorMessage(error) }),
    onSettled: finishRowOperation
  })

  const duplicateMutation = useMutation({
    retry: false,
    mutationFn: async (vehicle: Vehicle) => {
      const existing = await collectAllVehicles(vehicleService.getVehicles)
      for (let attempt = 1; attempt <= 3; attempt++) {
        const registration = duplicateReference(vehicle.registration_number, existing, attempt)
        try {
          return await vehicleService.createVehicle(duplicateVehiclePayload(vehicle, registration) as Vehicle)
        } catch (error) {
          // Retry only an explicit registration uniqueness conflict, never arbitrary writes.
          const failure = error as any
          if (attempt === 3 || !(failure?.response?.status === 409 || failure?.response?.data?.code === '23505') || !/registration|immatriculation|référence|vehicles.*unique/i.test(failure?.response?.data?.message || '')) throw error
          existing.push({ registration_number: registration } as Vehicle)
        }
      }
      throw new Error('Impossible de générer une référence de copie disponible.')
    },
    onSuccess: async (created, vehicle) => {
      setLastCopy(created)
      setSavedMessage(`Copie de « ${getVehicleDisplayName(vehicle)} » créée : ${getVehicleDisplayName(created)}.`)
      await queryClient.invalidateQueries({ queryKey: ['vehicles'] })
    },
    onError: (error, vehicle) => setRowError({ kind: 'duplicate', vehicle, message: vehicleErrorMessage(error) }),
    onSettled: finishRowOperation
  })

  const isSaving = createMutation.isLoading || updateMutation.isLoading
  const listActionsBlocked = isFetching || searchTerm.trim() !== searchQuery || isError || !vehiclesData || !!rowOperation || isSaving || isEditorOpen || !!libraryMode

  const handleSave = (vehicleData: Partial<Vehicle>) => {
    if (!(editingVehicle ? canUpdate : canCreate) || viewOnly || saveLock.current || rowOperationRef.current) return
    const payload = prepareVehicleFormPayload(vehicleData)
    saveLock.current = true
    setSavedMessage('')
    setLastCopy(null)
    if (editingVehicle?.id) {
      updateMutation.mutate({ id: editingVehicle.id, vehicle: payload })
    } else {
      createMutation.mutate(payload as Omit<Vehicle, 'id' | 'tenant_id' | 'created_at' | 'updated_at'>)
    }
  }

  const handleEdit = (vehicle: Vehicle) => {
    if (!canUpdate || listActionsBlocked || rowOperationRef.current) return
    setViewOnly(false)
    createMutation.reset()
    updateMutation.reset()
    setRowError(null)
    setInitialPreset(null)
    setEditingVehicle(vehicle)
    openEditor()
  }

  const handleDelete = (vehicle: Vehicle) => {
    if (!canDelete || !vehicle.id || listActionsBlocked || rowOperationRef.current) return
    if (confirm(`Supprimer le véhicule « ${getVehicleDisplayName(vehicle)} » ?`)) {
      setLastCopy(null)
      const operation: RowOperation = { kind: 'delete', vehicle }
      rowOperationRef.current = operation
      setRowOperation(operation)
      setRowError(null)
      setSavedMessage('')
      deleteMutation.mutate(vehicle)
    }
  }

  const handleDuplicate = (vehicle: Vehicle) => {
    if (!canCreate || !vehicle.id || listActionsBlocked || rowOperationRef.current) return
    const operation: RowOperation = { kind: 'duplicate', vehicle }
    setLastCopy(null)
    rowOperationRef.current = operation
    setRowOperation(operation)
    setRowError(null)
    setSavedMessage('')
    duplicateMutation.mutate(vehicle)
  }

  const handleCreate = () => {
    if (!canCreate || saveLock.current || rowOperationRef.current || isEditorOpen || libraryMode) return
    setViewOnly(false)
    createMutation.reset()
    updateMutation.reset()
    setRowError(null)
    setInitialPreset(null)
    setEditingVehicle(null)
    openEditor()
  }

  const handleConsult = (vehicle: Vehicle) => {
    if (!canConsult || listActionsBlocked) return
    setViewOnly(true)
    setInitialPreset(null)
    setEditingVehicle(vehicle)
    openEditor()
  }

  useEffect(() => {
    if (isFetching || isPreviousData || isError) return
    if (vehiclesData?.pagination && currentPage > vehiclesData.pagination.totalPages) {
      setCurrentPage(Math.max(vehiclesData.pagination.totalPages, 1))
    }
  }, [currentPage, vehiclesData?.pagination?.totalPages, isFetching, isPreviousData, isError])


  const vehicles = canConsult ? vehiclesData?.data || [] : []
  const pagination = vehiclesData?.pagination
  const totalItems = pagination?.total ?? vehicles.length
  const totalPages = Math.max(1, pagination?.totalPages || 1)
  const pageWindowStart = Math.max(1, Math.min(currentPage - 2, Math.max(1, totalPages - 4)))
  const pageWindowEnd = Math.min(totalPages, pageWindowStart + 4)
  const pageNumbers = Array.from({ length: pageWindowEnd - pageWindowStart + 1 }, (_, index) => pageWindowStart + index)

  // All filters are applied by the server before counting and pagination.
  const filteredVehicles = vehicles
  const hasActiveFilters = !!searchTerm.trim() || filterStatus !== 'all' || selectedAttributes.length > 0
  const resetFilters = () => {
    setSelectedAttributes([])
    setSearchTerm('')
    setSearchQuery('')
    setCurrentPage(1)
    setFilterStatus('all')
  }
  const listStatus = !canConsult ? '' : searchTerm.trim() !== searchQuery ? 'Recherche en cours dans toute la flotte…' : isFetching
    ? !vehiclesData ? 'Chargement des véhicules…'
      : isPreviousData ? `Chargement de la page ${currentPage}… Les véhicules de la page ${vehiclesData.pagination.page} restent affichés.`
        : 'Actualisation des véhicules…'
    : isError ? '' : vehiclesData ? `${totalItems} véhicule(s) correspondant(s) dans la flotte · ${vehicles.length} sur cette page.` : ''

  return (
    <>
    <div ref={listRef} hidden={isEditorOpen && canConsult} className="vehicle-settings max-w-7xl mx-auto space-y-0">
      {/* Toast */}
      <div role="status" aria-live="polite" aria-atomic="true">
        {savedMessage && <div className="mb-4 flex items-center gap-3 px-5 py-3.5 rounded-xl border-l-4 bg-slate-900 border-l-emerald-500 text-emerald-400">
          <CheckCircleIcon className="w-5 h-5" />
          <span className="text-sm font-semibold text-white">{savedMessage}</span>
          {lastCopy && canUpdate && <button type="button" disabled={listActionsBlocked} onClick={() => handleEdit(lastCopy)} className="shrink-0 rounded-lg border border-white/50 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50">Modifier la copie</button>}
          <button type="button" aria-label="Fermer la notification" onClick={() => setSavedMessage('')} className="ml-auto rounded-lg p-2 text-white focus-visible:ring-2 focus-visible:ring-white"><XMarkIcon className="w-4 h-4" /></button>
        </div>}
      </div>

      {/* ── CARGO HEADER BANNER ── */}
      <div className="cargo-bg rounded-none sm:rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden mb-6">
        <div className="h-1 w-full bg-gradient-to-r from-orange-500 via-amber-400 to-orange-600" />
        <div className="px-3 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:px-6 sm:py-5 sm:gap-5">
          <div className="flex items-center gap-4">
            <div className="relative w-14 h-14 rounded-xl border border-orange-200 dark:border-orange-500/30 bg-orange-50 dark:bg-orange-500/10 flex items-center justify-center flex-shrink-0">
              <TruckIcon className="w-7 h-7 text-orange-600 dark:text-orange-400" />
              <div className="absolute -top-1 -right-1 w-4.5 h-4.5 rounded-full bg-orange-500 flex items-center justify-center shadow-sm">
                <span className="text-[8px] font-black text-white">3D</span>
              </div>
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 ref={pageHeadingRef} tabIndex={-1} className="text-xl font-black text-slate-900 dark:text-white tracking-tight uppercase">Flotte Véhicules</h1>
                <span className="text-xs font-extrabold px-2 py-0.5 rounded border border-orange-300 dark:border-orange-500/40 bg-orange-50/50 dark:bg-orange-950/40 text-orange-600 dark:text-orange-400 uppercase tracking-widest">TMS</span>
              </div>
              <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400 font-mono">Espace de chargement et optimisation 3D</p>
            </div>
          </div>
          <div className="grid grid-cols-2 items-center gap-2 sm:flex sm:flex-wrap sm:justify-end">
          <button type="button" disabled={!canConsult || searchTerm.trim() !== searchQuery || isFetching || isError || !vehiclesData || !!rowOperation || isSaving} onClick={() => setLibraryMode('export')}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-semibold text-slate-700 hover:border-orange-300 disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200">
            <ArrowDownTrayIcon className="h-4 w-4" />Exporter
          </button>
          <button type="button" disabled={!canCreate || !!rowOperation || isSaving} onClick={() => setLibraryMode('import')}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-semibold text-slate-700 hover:border-orange-300 disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200">
            <ArrowUpTrayIcon className="h-4 w-4" />Importer
          </button>
          <button ref={modelsTriggerRef} type="button" disabled={!canCreate || !!rowOperation || isSaving} onClick={() => setLibraryMode('presets')}
            className="inline-flex items-center gap-2 rounded-xl border border-orange-200 bg-orange-50 px-3 py-2.5 text-sm font-bold text-orange-700 hover:bg-orange-100 disabled:cursor-not-allowed disabled:opacity-40 dark:border-orange-500/30 dark:bg-orange-500/10 dark:text-orange-300">
            <SparklesIcon className="h-4 w-4" />Modèles
          </button>
          <button disabled={!canCreate || !!rowOperation || isSaving} onClick={handleCreate}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-orange-700 hover:bg-orange-800 text-white rounded-xl font-bold text-sm disabled:cursor-not-allowed disabled:opacity-40 transition-all">
            <PlusIcon className="w-4 h-4" />
            Nouveau véhicule
          </button>
          </div>
        </div>
      </div>

      <div className="px-0 sm:px-0 space-y-5 warehouse-grid">
        {!canWrite && canConsult && <p role="status" className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:bg-amber-950 dark:text-amber-200">Consultation seule : vous pouvez ouvrir les fiches et exporter les véhicules. Les modifications sont indisponibles avec votre accès actuel.</p>}
        {(accessLoading || authLoading) && <p role="status" className="p-3 text-sm text-slate-600 dark:text-slate-300">Vérification de votre accès à la flotte…</p>}
        {!canConsult && !accessLoading && !authLoading && <p role="alert" className="rounded-xl border border-red-200 bg-red-50 p-4 text-red-800">La consultation de la flotte nécessite un accès en lecture et la permission « Voir les véhicules ».</p>}
        {canWrite && canConsult && (!canCreate || !canUpdate || !canDelete) && <p className="text-sm text-slate-600 dark:text-slate-300">Vos permissions autorisent : consultation et export{canCreate ? ', création et duplication' : ''}{canUpdate ? ', modification' : ''}{canDelete ? ', suppression' : ''}.</p>}
        {/* Stats — manifeste style */}
        {canConsult && !fleetSummary && !summaryError && <p role="status" className="text-sm text-slate-600 dark:text-slate-300">Chargement des indicateurs…</p>}
        {canConsult && summaryError && <div role="alert" className="flex flex-wrap items-center gap-3 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/30 dark:text-red-200"><p>Indicateurs indisponibles.</p><button type="button" disabled={summaryFetching} onClick={() => void refetchSummary()} className="rounded-lg border border-current px-3 py-2 font-semibold disabled:opacity-40">Réessayer les indicateurs</button></div>}
        {fleetSummary && <section aria-label="Indicateurs de la flotte" aria-busy={summaryFetching}>
        <details className="rounded-xl border border-slate-200 bg-white p-3 text-sm dark:border-slate-700 dark:bg-slate-900 sm:hidden"><summary className="min-h-8 cursor-pointer font-semibold text-slate-800 dark:text-slate-100">Indicateurs · {fleetSummary.total} véhicule(s)</summary><dl className="mt-3 space-y-2 text-slate-700 dark:text-slate-200"><div><dt>Volume intérieur connu</dt><dd>{fleetSummary.volume.value == null ? 'Non renseigné' : <Measure value={fleetSummary.volume.value} from="m3" quantity="volume" />} · {fleetSummary.volume.known} espace(s)</dd></div><div><dt>Charges utiles enregistrées</dt><dd>{fleetSummary.payload.value == null ? 'Non renseignées' : <Measure value={fleetSummary.payload.value} from="kg" quantity="mass" />} · {fleetSummary.payload.known}/{fleetSummary.payload.applicable} renseignées</dd></div><div><dt>Actifs / frigorifiques / ADR</dt><dd>{fleetSummary.active} / {fleetSummary.reefer} / {fleetSummary.adr}</dd></div></dl></details>
        <div className="hidden grid-cols-2 xl:grid-cols-4 gap-3 sm:grid">
          {[
            { label: 'Volume intérieur', value: fleetSummary.volume.value == null ? '—' : <Measure value={fleetSummary.volume.value} from="m3" quantity="volume" />, icon: SquareDimensions, accent: '#0ea5e9', sub: `${fleetSummary.volume.known} espace(s) aux dimensions renseignées` },
            { label: 'Charges utiles renseignées', value: fleetSummary.payload.value == null ? '—' : <Measure value={fleetSummary.payload.value} from="kg" quantity="mass" />, icon: Weight, accent: '#10b981', sub: `${fleetSummary.payload.known}/${fleetSummary.payload.applicable} espace(s) aux capacités renseignées · valeurs enregistrées` },
            { label: 'Véhicules actifs', value: fleetSummary.active, total: fleetSummary.total, icon: TruckIcon, accent: '#f59e0b', sub: '' },
            {
              label: 'Équipements',
              value: (
                <span className="inline-flex items-baseline gap-1.5 whitespace-nowrap">
                  <span className="text-sky-500 dark:text-sky-400">{fleetSummary.reefer}</span>
                  <span className="text-xs font-sans font-semibold text-slate-600 dark:text-slate-300">Frigo</span>
                  <span className="text-slate-200 dark:text-slate-700 text-xl font-light mx-0.5">/</span>
                  <span className="text-amber-500 dark:text-amber-400">{fleetSummary.adr}</span>
                  <span className="text-xs sm:text-xs font-sans font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">ADR</span>
                </span>
              ),
              icon: WrenchScrewdriverIcon,
              accent: '#8b5cf6',
              sub: 'spécialisés'
            },
          ].map(s => {
            const Icon = s.icon
            return (
              <div key={s.label} className="relative cargo-corner bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 p-4 overflow-hidden">
                <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 19px, currentColor 20px)', backgroundSize: '100% 20px' }} />
                <div className="relative">
                  <div className="flex items-start justify-between mb-3">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: s.accent + '18', border: `1.5px solid ${s.accent}40` }}>
                      <Icon className="w-4.5 h-4.5" style={{ color: s.accent }} />
                    </div>
                    <span className="text-[9px] font-black uppercase tracking-widest" style={{ color: s.accent }}>■</span>
                  </div>
                  <p className="text-3xl font-black text-slate-900 dark:text-white font-mono" style={{ textShadow: `0 0 20px ${s.accent}30` }}>
                    {s.total !== undefined ? (
                      <>
                        {s.value}
                        <span className="ml-1 text-sm font-medium text-slate-400">/ {s.total}</span>
                      </>
                    ) : (
                      s.value
                    )}
                  </p>
                  <p className="text-xs font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400 mt-0.5">{s.label}</p>
                  <p className="text-xs text-slate-400 dark:text-slate-600">{s.sub}</p>
                </div>
              </div>
            )
          })}
        </div></section>}

        <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 px-4 py-3">
          <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
            <div className="relative flex-1">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input type="text" aria-label="Rechercher dans toute la flotte" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                placeholder="Recherche: immatriculation, type…"
                className="w-full pl-9 pr-4 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-sm font-mono text-slate-900 dark:text-white focus:ring-2 focus:ring-orange-500/40 focus:border-orange-400 transition-all placeholder:text-slate-400" />
            </div>
            <select aria-label="Filtrer les véhicules par statut" value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setCurrentPage(1) }}
              className="px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-sm text-slate-700 dark:text-slate-300 focus:ring-2 focus:ring-orange-500/40 transition-all">
              <option value="all">Tous statuts</option>
              <option value="active">● Actifs</option>
              <option value="inactive">○ Inactifs</option>
              <option value="maintenance">◐ Maintenance</option>
              <option value="out_of_service">Hors service</option>
              <option value="favorites">⭐ Favoris</option>
            </select>
            <select aria-label="Véhicules par page" disabled={isFetching || !!rowOperation || isSaving} value={pageSize} onChange={(event) => { setPageSize(Number(event.target.value)); setCurrentPage(1) }}
              className="px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-sm text-slate-700 dark:text-slate-300 focus:ring-2 focus:ring-orange-500/40 transition-all">
              <option value={10}>10 / page</option>
              <option value={25}>25 / page</option>
              <option value={50}>50 / page</option>
              <option value={100}>100 / page</option>
            </select>
          </div>

          {/* Chips Filter Row */}
          <div className="flex flex-wrap items-center gap-2 pt-3 mt-3 border-t border-slate-100 dark:border-slate-800">
            <span className="text-xs font-extrabold uppercase tracking-wider text-slate-400 dark:text-slate-500 mr-1">
              Équipements :
            </span>
            {[
              { id: 'reefer', label: 'Frigorifique', icon: Thermometer },
              { id: 'adr', label: 'ADR', icon: AlertTriangle },
            ].map(chip => {
              const isSelected = selectedAttributes.includes(chip.id)
              const Icon = chip.icon
              return (
                <button
                  key={chip.id}
                  type="button"
                   aria-pressed={isSelected}
                  onClick={() => toggleAttribute(chip.id)}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold transition-all border ${isSelected
                    ? 'bg-orange-700 text-white border-orange-700'
                    : 'bg-slate-50 dark:bg-slate-800/80 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
                    }`}
                >
                  <Icon className={`w-3.5 h-3.5 ${isSelected ? 'text-white' : 'text-slate-400'}`} />
                  <span>{chip.label}</span>
                </button>
              )
            })}

            {(selectedAttributes.length > 0 || searchTerm || filterStatus !== 'all') && (
              <button
                type="button"
                onClick={resetFilters}
                className="text-xs text-orange-600 dark:text-orange-400 hover:underline font-semibold ml-auto flex items-center gap-1"
              >
                <XMarkIcon className="w-3.5 h-3.5" />
                Réinitialiser filtres
              </button>
            )}
          </div>
        </div>

        <p role="status" aria-live="polite" aria-atomic="true" className="sr-only">{listStatus}</p>
        {isError && <div role="alert" className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/30 dark:text-red-200">
          <div><p className="font-semibold">Impossible de charger les véhicules.</p><p>{vehicleErrorMessage(listError)}</p>{vehiclesData && <p className="mt-1">Les dernières données connues restent affichées. Actualisez la liste avant d’agir.</p>}</div>
          <button type="button" disabled={isFetching} onClick={() => void refetch()} className="rounded-lg border border-current px-3 py-2 font-semibold disabled:opacity-40">{isFetching ? 'Chargement…' : 'Réessayer le chargement'}</button>
        </div>}
        <div role="status" aria-live="polite" aria-atomic="true" className="text-sm text-slate-700 dark:text-slate-200">{rowOperation ? `${rowOperation.kind === 'delete' ? 'Suppression' : 'Duplication'} de « ${getVehicleDisplayName(rowOperation.vehicle)} » en cours…` : ''}</div>
        {rowError && <div role="alert" className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/30 dark:text-red-200">
          <div><p className="font-semibold">{rowError.kind === 'delete' ? 'Suppression' : 'Duplication'} de « {getVehicleDisplayName(rowError.vehicle)} » impossible.</p><p>{rowError.message}</p></div>
          <button type="button" aria-label="Fermer l’erreur d’opération" onClick={() => setRowError(null)} className="rounded-lg p-2 focus-visible:ring-2 focus-visible:ring-current"><XMarkIcon className="h-5 w-5" /></button>
        </div>}
        {canConsult && <div role="region" aria-label="Liste des véhicules" aria-busy={isFetching} className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden shadow-sm">
          <div className="space-y-3 p-3 sm:hidden">
            {isLoading ? <p className="p-5 text-sm text-slate-600">Chargement des véhicules…</p> : filteredVehicles.map(vehicle => <article key={vehicle.id} aria-label={`Véhicule ${getVehicleDisplayName(vehicle)}`} className="min-w-0 space-y-3 rounded-xl border border-slate-200 p-3 text-sm dark:border-slate-700">
              <button type="button" disabled={!canConsult || listActionsBlocked} onClick={() => handleConsult(vehicle)} className="min-h-10 w-full break-all text-left font-bold text-slate-900 underline underline-offset-4 dark:text-white">{getVehicleDisplayName(vehicle)}</button>
              <p className="text-slate-700 dark:text-slate-200">{getVehicleTypeLabel(vehicle.type)}{vehicle.vehicle_subtype ? ` · ${getVehicleSubtypeLabel(vehicle.vehicle_subtype)}` : ''} · {getStatusLabel(vehicle.status)}</p>
              <dl className="flex flex-wrap gap-x-4 gap-y-2 text-slate-700 dark:text-slate-200">
                <div><dt className="font-medium">Charge utile enregistrée</dt><dd>{isFleetTractor(vehicle) ? 'Non applicable' : <Measure value={optionalVehicleNumber(vehicle.capacity_weight)} from="kg" quantity="mass" />}</dd></div>
                <div><dt className="font-medium">Volume intérieur calculé</dt><dd>{isFleetTractor(vehicle) ? 'Non applicable' : <Measure value={vehicleInteriorVolume(vehicle.length, vehicle.width, vehicle.height)} from="m3" quantity="volume" />}</dd></div>
              </dl>
              <details className="text-slate-600 dark:text-slate-300"><summary className="min-h-8 cursor-pointer">Configuration et équipements</summary><dl className="my-2 space-y-2"><div><dt>Dimensions</dt><dd><Dimensions length={vehicle.length} width={vehicle.width} height={vehicle.height} from="mm" quantity="vehicleLength" /></dd></div>{vehicle.capacity_volume != null && !isFleetTractor(vehicle) && <div><dt>Volume déclaré enregistré</dt><dd><Measure value={optionalVehicleNumber(vehicle.capacity_volume)} from="m3" quantity="volume" /></dd></div>}</dl><p className="mb-2">{[vehicle.has_reefer_unit && 'Frigorifique', vehicle.is_hazardous_compatible && 'Compatibilité ADR déclarée'].filter(Boolean).join(' · ') || 'Aucun équipement frigorifique ou ADR déclaré'}</p><StarRating rating={vehicle.favorite_rating} readonly size="sm" /></details>
              <div className="flex flex-wrap gap-2 border-t border-slate-200 pt-3 dark:border-slate-700">{[
                { label: 'Modifier', allowed: canUpdate, action: () => handleEdit(vehicle) },
                { label: 'Dupliquer', allowed: canCreate, action: () => handleDuplicate(vehicle) },
                { label: 'Supprimer', allowed: canDelete, action: () => handleDelete(vehicle) },
              ].map(action => <button key={action.label} type="button" disabled={!action.allowed || listActionsBlocked || !vehicle.id} onClick={action.action} className="min-h-10 rounded-lg border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-700 disabled:opacity-40 dark:border-slate-600 dark:text-slate-200">{action.label}</button>)}</div>
            </article>)}
            {!isFetching && !isError && !vehicles.length && <div className="space-y-3 p-4 text-center text-sm"><p>{hasActiveFilters ? 'Aucun véhicule ne correspond aux filtres dans la flotte.' : 'Votre flotte ne contient aucun véhicule.'}</p>{hasActiveFilters ? <button type="button" onClick={resetFilters} className="min-h-10 underline">Effacer les filtres</button> : <button type="button" disabled={!canCreate} onClick={handleCreate} className="min-h-10 rounded-lg bg-orange-700 p-3 text-white disabled:opacity-40">Créer votre premier véhicule</button>}</div>}
          </div>
          <div className="relative hidden overflow-x-auto sm:block">
            {isFetching && (
              <div aria-hidden="true" className="absolute inset-0 bg-white/70 dark:bg-slate-900/70 backdrop-blur-[2px] z-10 flex items-center justify-center">
                <div className="flex items-center gap-3 px-4 py-2.5 rounded-xl bg-slate-900/80 dark:bg-slate-700/80 shadow-lg">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-orange-400 border-t-transparent"></div>
                  <span className="text-xs font-bold text-white uppercase tracking-wider">Chargement…</span>
                </div>
              </div>
            )}
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50/80 dark:bg-slate-800/60 border-b border-slate-200 dark:border-slate-700">
                  {['Référence / immatriculation', 'Type / Sous-type', 'Dimensions', 'Capacité', 'Équipements', 'Favoris', 'Statut', 'Actions'].map(h => (
                    <th key={h} className={`py-3 px-4 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 ${h === 'Actions' ? 'text-right' : 'text-left'}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  Array.from({ length: pageSize }).map((_, i) => (
                    <tr key={i} aria-hidden="true" className="border-b border-slate-100 dark:border-slate-800 animate-pulse">
                      <td key={0} className="py-3.5 px-4">
                        <div className="flex items-center gap-2.5">
                          <div className="h-8 w-8 bg-slate-200 dark:bg-slate-700 rounded-lg"></div>
                          <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-24"></div>
                        </div>
                      </td>
                      <td key={1} className="py-3.5 px-4">
                        <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-28 mb-1.5"></div>
                        <div className="h-3 bg-slate-100 dark:bg-slate-800 rounded w-20"></div>
                      </td>
                      {Array.from({ length: 6 }).map((__, j) => (
                        <td key={j + 2} className="py-3.5 px-4">
                          <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-3/4"></div>
                        </td>
                      ))}
                    </tr>
                  ))
                ) : !vehiclesData && isError ? (
                  <tr><td colSpan={8} className="p-12 text-center text-sm text-slate-600 dark:text-slate-300">La liste est indisponible. Réessayez le chargement pour consulter vos véhicules.</td></tr>
                ) : (
                  <>
                    {filteredVehicles.map((vehicle: Vehicle) => {
                      const { icon: TypeIcon, color: typeColor } = getVehicleTypeStyle(vehicle.type)
                      const tractor = isFleetTractor(vehicle)
                      const interiorVolume = tractor ? null : vehicleInteriorVolume(vehicle.length, vehicle.width, vehicle.height)
                      const declaredVolume = tractor ? null : optionalVehicleNumber(vehicle.capacity_volume)
                      const hasDeclaredVolume = declaredVolume != null && (interiorVolume == null || Math.abs(declaredVolume - interiorVolume) > 0.001)
                      return (
                        <tr key={vehicle.id} className="group border-b border-slate-100 last:border-0 transition-all duration-150 hover:bg-orange-50/30 dark:border-slate-800 dark:hover:bg-slate-800/30">
                          <td className="py-3.5 px-4">
                            <div className="flex items-center gap-2.5">
                              <div
                                className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg border flex-shrink-0 transition-transform group-hover:scale-105"
                                style={{ backgroundColor: typeColor + '18', borderColor: typeColor + '40', color: typeColor }}
                              >
                                <TypeIcon className="w-4 h-4" />
                              </div>
                              <button type="button" disabled={!canConsult || listActionsBlocked} onClick={() => handleConsult(vehicle)} aria-label={`Consulter le véhicule ${getVehicleDisplayName(vehicle)}`} className="min-h-10 break-words text-left font-bold text-sm text-slate-900 underline decoration-slate-300 underline-offset-4 dark:text-white">{getVehicleDisplayName(vehicle)}</button>
                            </div>
                          </td>
                          <td className="py-3.5 px-4">
                            <div className="flex flex-col">
                              <span className="font-semibold text-sm text-slate-800 dark:text-slate-200">
                                {getVehicleTypeLabel(vehicle.type)}
                              </span>
                              {vehicle.vehicle_subtype && (
                                <span className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 font-medium">
                                  {getVehicleSubtypeLabel(vehicle.vehicle_subtype)}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="py-3.5 px-4">
                            {vehicle.length && vehicle.width && vehicle.height ? (
                              <span className="text-xs font-mono tabular-nums text-slate-600 dark:text-slate-400 bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded-md">
                                <Dimensions length={vehicle.length} width={vehicle.width} height={vehicle.height} quantity="vehicleLength" />
                              </span>
                            ) : (
                              <span className="text-slate-400">—</span>
                            )}
                          </td>
                          <td className="py-3.5 px-4 text-sm text-slate-600 dark:text-slate-400">
                            <div className="flex flex-col gap-0.5">
                              {vehicle.capacity_weight != null && !tractor ? (
                                <span className="flex items-center gap-1.5 text-xs">
                                  <Weight className="w-3.5 h-3.5 text-emerald-500" />
                                  <span className="font-semibold text-slate-700 dark:text-slate-300"><Measure value={vehicle.capacity_weight} from="kg" quantity="mass" /></span>
                                </span>
                              ) : null}
                              {interiorVolume != null ? (
                                <span className="flex items-center gap-1.5 text-xs">
                                  <SquareDimensions className="w-3.5 h-3.5 text-sky-500" />
                                  <span title="Volume intérieur calculé" className="font-semibold text-slate-700 dark:text-slate-300"><Measure value={interiorVolume} from="m3" quantity="volume" /></span>
                                </span>
                              ) : null}
                              {hasDeclaredVolume && <span className="text-xs text-slate-600 dark:text-slate-300">Volume déclaré : <Measure value={declaredVolume} from="m3" quantity="volume" /></span>}
                              {(vehicle.capacity_weight == null || tractor) && interiorVolume == null && !hasDeclaredVolume && <span className="text-slate-400">{tractor ? 'Non applicable' : 'Non renseigné'}</span>}
                            </div>
                          </td>
                          <td className="py-3.5 px-4">
                            <div className="flex items-center gap-2">
                              {vehicle.has_reefer_unit && (
                                <div className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-sky-50 dark:bg-sky-900/20 border border-sky-200 dark:border-sky-800" title="Unité de Réfrigération">
                                  <Thermometer className="w-4 h-4 text-sky-500 dark:text-sky-400" />
                                </div>
                              )}
                              {vehicle.is_hazardous_compatible && (
                                <div className="hazmat-stripe flex items-center gap-1.5 px-2 py-1 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800" title="Compatible ADR (Marchandises Dangereuses)">
                                  <ExclamationTriangleIcon className="w-4 h-4 text-amber-500 dark:text-amber-400" />
                                </div>
                              )}
                              {!vehicle.has_reefer_unit && !vehicle.is_hazardous_compatible && (
                                <span className="text-slate-400">—</span>
                              )}
                            </div>
                          </td>
                          <td className="py-3.5 px-4">
                            <div className="flex items-center gap-2">
                              <StarRating rating={vehicle.favorite_rating || 0} readonly size="sm" />
                              {(vehicle.favorite_rating || 0) > 0 && (
                                <span className="text-xs font-bold text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 px-1.5 py-0.5 rounded-md">
                                  {vehicle.favorite_rating}/5
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="py-3.5 px-4">
                            <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${getStatusBadgeClasses(vehicle.status)}`}>
                              <span className="h-1.5 w-1.5 rounded-full bg-current" />
                              {getStatusLabel(vehicle.status)}
                            </span>
                          </td>
                          <td className="py-3.5 px-4 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <button disabled={!canUpdate || listActionsBlocked || !vehicle.id}
                                onClick={() => handleEdit(vehicle)}
                                aria-label={`Modifier le véhicule ${getVehicleDisplayName(vehicle)}`}
                                title="Modifier ce véhicule"
                                className="rounded-lg p-2 text-slate-500 transition-all hover:bg-orange-50 hover:text-orange-600 dark:hover:bg-orange-900/20 dark:hover:text-orange-400 focus-visible:ring-2 focus-visible:ring-orange-500 disabled:cursor-not-allowed disabled:opacity-40"
                              >
                                <PencilIcon className="w-4 h-4" />
                              </button>
                              <button disabled={!canCreate || listActionsBlocked || !vehicle.id}
                                onClick={() => handleDuplicate(vehicle)}
                                title="Dupliquer ce véhicule"
                                 aria-label={rowOperation?.kind === 'duplicate' && rowOperation.vehicle.id === vehicle.id ? 'Duplication en cours' : `Dupliquer le véhicule ${getVehicleDisplayName(vehicle)}`}
                                className="rounded-lg p-2 text-slate-500 transition-all hover:bg-slate-50 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-300 focus-visible:ring-2 focus-visible:ring-orange-500 disabled:cursor-not-allowed disabled:opacity-40"
                              >
                                {rowOperation?.kind === 'duplicate' && rowOperation.vehicle.id === vehicle.id ? <span aria-hidden="true" className="block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" /> : <DocumentDuplicateIcon className="w-4 h-4" />}
                              </button>
                              <button disabled={!canDelete || listActionsBlocked || !vehicle.id}
                                onClick={() => handleDelete(vehicle)}
                                title="Supprimer ce véhicule"
                                aria-label={rowOperation?.kind === 'delete' && rowOperation.vehicle.id === vehicle.id ? 'Suppression en cours' : `Supprimer le véhicule ${getVehicleDisplayName(vehicle)}`}
                                className="rounded-lg p-2 text-slate-500 transition-all hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20 dark:hover:text-red-400 focus-visible:ring-2 focus-visible:ring-orange-500 disabled:cursor-not-allowed disabled:opacity-40"
                              >
                                {rowOperation?.kind === 'delete' && rowOperation.vehicle.id === vehicle.id ? <span aria-hidden="true" className="block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" /> : <TrashIcon className="w-4 h-4" />}
                              </button>
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                    {filteredVehicles.length === 0 && !isError && !isFetching && (
                      <tr>
                        <td colSpan={8} className="py-16 text-center">
                          <div className="flex flex-col items-center gap-3">
                            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-400 dark:bg-slate-800">
                              <TruckIcon className="w-6 h-6" />
                            </div>
                            <p className="text-sm text-slate-500 dark:text-slate-400">
                              {hasActiveFilters
                                ? 'Aucun véhicule ne correspond aux filtres dans la flotte.'
                                : totalItems === 0 ? 'Votre flotte ne contient aucun véhicule.' : 'Aucun véhicule sur cette page.'
                              }
                            </p>
                            {hasActiveFilters && <button type="button" onClick={resetFilters} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 dark:border-slate-600 dark:text-slate-200">Effacer les filtres</button>}
                            {!hasActiveFilters && totalItems === 0 && (
                              <button disabled={!canCreate || !!rowOperation || isSaving}
                                onClick={handleCreate}
                                className="mt-1 inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-slate-800 dark:bg-amber-500 dark:text-slate-900 dark:hover:bg-amber-400"
                              >
                                <PlusIcon className="w-4 h-4" />
                                Créer votre premier véhicule
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                )}
              </tbody>
            </table>
          </div>

          {vehiclesData && <div className="flex flex-col gap-3 border-t border-slate-100 dark:border-slate-800 sm:flex-row sm:items-center sm:justify-between px-4 py-3">
            <p className="text-xs font-mono font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
              Page <span className="text-slate-900 dark:text-white">{currentPage}</span> / {totalPages}
              <span className="mx-2 text-slate-300 dark:text-slate-700">·</span>
              <span className="text-slate-900 dark:text-white">{totalItems}</span> véhicule{totalItems !== 1 ? 's' : ''}
            </p>
            <div className="flex items-center justify-between gap-1.5">
              <button
                onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                disabled={currentPage === 1 || listActionsBlocked}
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-2 text-xs font-semibold text-slate-600 dark:text-slate-300 transition-all hover:bg-slate-50 dark:hover:bg-slate-800 hover:border-slate-300 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <ChevronLeftIcon className="w-3.5 h-3.5" />
                Préc.
              </button>
              {pageNumbers.map((page) => (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  disabled={listActionsBlocked}
                  aria-current={page === currentPage ? 'page' : undefined}
                  className={`hidden min-w-[36px] h-9 rounded-lg text-xs font-bold transition-all sm:block ${page === currentPage
                    ? 'bg-orange-700 text-white'
                    : 'border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'
                    }`}
                >
                  {page}
                </button>
              ))}
              <button
                onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                disabled={currentPage === totalPages || listActionsBlocked}
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-2 text-xs font-semibold text-slate-600 dark:text-slate-300 transition-all hover:bg-slate-50 dark:hover:bg-slate-800 hover:border-slate-300 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Suiv.
                <ChevronRightIcon className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>}
        </div>}

        {libraryMode && canConsult && (
          <VehicleLibraryDialog
            fallbackFocus={pageHeadingRef}
            mode={libraryMode}
            canWrite={canCreate}
            registrationRequired={registrationRequired}
            visibleVehicles={filteredVehicles}
            filteredParams={hasActiveFilters ? { search: searchQuery, ...vehicleSearchAliases(searchQuery), status: filterStatus === 'all' || filterStatus === 'favorites' ? undefined : filterStatus, favorites: filterStatus === 'favorites', reefer: selectedAttributes.includes('reefer'), adr: selectedAttributes.includes('adr') } : undefined}
            onClose={() => setLibraryMode(null)}
            onImported={() => { queryClient.invalidateQueries({ queryKey: ['vehicles'] }) }}
            onPreset={preset => {
              createMutation.reset()
              updateMutation.reset()
              setInitialPreset(preset)
              setEditingVehicle(null)
              setViewOnly(false)
              setLibraryMode(null)
              openEditor(modelsTriggerRef.current)
            }}
          />
        )}

      </div>
    </div>
        {isEditorOpen && canConsult && (
          <VehicleEditorPage
            vehicle={editingVehicle}
            readOnly={viewOnly || (editingVehicle ? !canUpdate : !canCreate)}
            initialPreset={initialPreset}
            registrationRequired={registrationRequired}
            onSave={handleSave}
            onClose={() => {
              setIsEditorOpen(false)
              setEditingVehicle(null)
              setInitialPreset(null)
            }}
            isLoading={createMutation.isLoading || updateMutation.isLoading}
            saveError={createMutation.error || updateMutation.error ? vehicleErrorMessage(createMutation.error || updateMutation.error) : undefined}
          />
        )}
    </>
  )
}

function getVehicleTypeStyle(type: string | undefined): { icon: React.ComponentType<{ className?: string }>, color: string } {
  switch (type) {
    case 'CONTAINER':
      return { icon: CubeIcon, color: '#0ea5e9' }        // sky-500
    case 'SEMI_TRAILER':
      return { icon: TruckIcon, color: '#f59e0b' }       // amber-500
    case 'RIGID_TRUCK':
      return { icon: TruckIcon, color: '#10b981' }       // emerald-500
    case 'VAN':
      return { icon: TruckIcon, color: '#8b5cf6' }       // violet-500
    case 'TRAILER':
      return { icon: TruckIcon, color: '#6366f1' }       // indigo-500
    case 'TRUCK':
      return { icon: TruckIcon, color: '#f97316' }       // orange-500
    default:
      return { icon: TruckIcon, color: '#94a3b8' }       // slate-400
  }
}

function getStatusBadgeClasses(status: string | undefined): string {
  switch (status) {
    case 'active':
      return 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400'
    case 'maintenance':
      return 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-900/30 dark:text-amber-400'
    case 'inactive':
      return 'border-slate-200 bg-slate-50 text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400'
    default:
      return 'border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-400'
  }
}

function getStatusLabel(status: string | undefined): string {
  if (!status) return '-'
  const labels: Record<string, string> = {
    active: 'Actif',
    inactive: 'Inactif',
    maintenance: 'Maintenance',
    out_of_service: 'Hors service'
  }
  return labels[status] || status
}


function VehicleEditorPage({
  vehicle,
  initialPreset,
  registrationRequired,
  onSave,
  onClose,
  isLoading,
  saveError,
  readOnly = false,
}: {
  vehicle: Vehicle | null
  initialPreset: VehicleEquipmentPreset | null
  registrationRequired: boolean
  onSave: (vehicle: Partial<Vehicle>) => void
  onClose: () => void
  isLoading: boolean
  saveError?: string
  readOnly?: boolean
}) {
  const [formData, setFormData] = useState<Partial<Vehicle>>(() => {
    const initialVehicle: Partial<Vehicle> = vehicle || {
    registration_number: '',
    type: 'RIGID_TRUCK',
    depot_site_id: '',
    depot_address_id: '',
    vehicle_subtype: null,
    container_type: null,
    brand: '',
    model: '',
    year: null,
    capacity_volume: null,
    capacity_weight: null,
    empty_weight: null,
    length: null,
    width: null,
    height: null,
    fuel_type: null,
    euro_emission_standard: null,
    has_reefer_unit: false,
    reefer_temperature_range: null,
    is_hazardous_compatible: false,
    hazardous_certifications: [],
    has_tail_lift: false,
    tail_lift_capacity: null,
    has_loading_ramp: false,
    is_overweight_permitted: false,
    max_weight_permitted: null,
    total_mileage: null,
    fuel_consumption_average: null,
    maintenance_cost_per_km: null,
    breakdown_rate: null,
    availability_rate: null,
    average_rating: null,
    max_driving_distance_per_day: null,
    preferred_regions: [],
    restricted_regions: [],
    requires_special_license: false,
    required_license_type: '',
    hourly_cost: null,
    cost_per_km: null,
    daily_fixed_cost: null,
    special_equipment: [],
    has_gps_tracking: true,
    has_tachograph: false,
    has_video_surveillance: false,
    driver_ids: null,
    max_floor_pressure: null,
    pressure_cell_target_size: null,
    target_cg_x: null,
    target_cg_y: null,
    max_cg_deviation: null,
    axle_configuration: null,
    status: 'active',
    favorite_rating: 0
    }
    return initialPreset && !vehicle ? applyPresetToForm(initialVehicle, initialPreset) : initialVehicle
  })

  const [selectedPresetId, setSelectedPresetId] = useState<string | null>(initialPreset?.id || null)
  const [formError, setFormError] = useState('')
  const [registrationFocusRequest, setRegistrationFocusRequest] = useState(0)
  const [sectionProgress, setSectionProgress] = useState<VehicleCompletion>({ completed: 0, applicable: 0 })
  const baseline = useRef(vehicleFormFingerprint(formData))
  const [discardRequested, setDiscardRequested] = useState(false)
  const [profilePickerOpen, setProfilePickerOpen] = useState(false)
  const [profileUndo, setProfileUndo] = useState<{ data: Partial<Vehicle>; presetId: string | null } | null>(null)
  const profileTriggerRef = useRef<HTMLButtonElement>(null)
  const formRef = useRef<HTMLFormElement>(null)
  const hasMeasurementDraft = useMeasurementDrafts(formRef)
  const isDirty = !readOnly && (hasMeasurementDraft || vehicleFormFingerprint(formData) !== baseline.current)
  const requestClose = () => { if (isLoading) return; if (isDirty) setDiscardRequested(true); else onClose() }
  const titleId = useId()
  const pageRef = useRef<HTMLElement>(null)
  const [pageHeight, setPageHeight] = useState<number>()
  useLayoutEffect(() => {
    const page = pageRef.current
    if (!page) return
    const fit = () => setPageHeight(Math.max(240, (window.visualViewport?.height ?? window.innerHeight) + (window.visualViewport?.offsetTop ?? 0) - page.getBoundingClientRect().top))
    fit()
    page.focus({ preventScroll: true })
    const observer = new ResizeObserver(fit)
    if (page.parentElement) observer.observe(page.parentElement)
    window.addEventListener('resize', fit)
    window.visualViewport?.addEventListener('resize', fit)
    return () => {
      observer.disconnect()
      window.removeEventListener('resize', fit)
      window.visualViewport?.removeEventListener('resize', fit)
    }
  }, [])

  useUnsavedChangesWarning(isDirty || isLoading)

  // Déterminer si le véhicule courant est un tracteur (utilisé pour masquer l'espace de chargement)
  const isTractorVehicle: boolean =
    isFleetTractor(formData) ||
    (selectedPresetId != null && selectedPresetId.startsWith('tractor_'))
  // Déduire le preset à l'ouverture du formulaire
  useEffect(() => {
    if (initialPreset && !vehicle) {
      return
    }
    if (vehicle) {
      const deducedPreset = deducePresetFromVehicle(vehicle)
      setSelectedPresetId(deducedPreset?.id || null)
      const isTracteur = deducedPreset?.category === 'tracteur' ||
        (vehicle.vehicle_subtype && typeof vehicle.vehicle_subtype === 'string' && vehicle.vehicle_subtype.startsWith('tracteur')) ||
        (vehicle as any).isTractor === true
      if (isTracteur) {
        setFormData(prev => ({ ...prev, isTractor: true }))
      }
    } else {
      setSelectedPresetId(null)
    }
  }, [vehicle, initialPreset])

  const requestProfileChange = () => {
    if (isLoading || readOnly) return
    const invalid = formRef.current?.querySelector<HTMLInputElement>('[data-measurement-input][aria-invalid="true"]')
    if (invalid) { invalid.reportValidity(); return }
    setProfilePickerOpen(true)
  }


  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (isLoading || readOnly) return
    const registration = formData.registration_number?.trim() || null
    if ((registrationRequired && !registration) || (registration && registration.length > 50)) {
      setFormError(registrationRequired ? 'Renseignez une immatriculation ou une référence de 1 à 50 caractères.' : 'L’immatriculation ne peut pas dépasser 50 caractères.')
      setRegistrationFocusRequest(request => request + 1)
      return
    }
    setFormError('')

    const sanitized = { ...formData, registration_number: registration }

    try {
      onSave(sanitized)
    } catch (error) {
      setFormError((error as Error).message)
    }
  }

  return (
    <section ref={pageRef} data-vehicle-editor-page aria-labelledby={titleId} tabIndex={-1} className="vehicle-editor-page w-full min-w-0" style={{ height: pageHeight ?? '100dvh' }}>
      {/* Workspace de configuration */}
      <div
        className="vehicle-editor-shell relative flex h-full w-full min-w-0 flex-col overflow-hidden bg-white dark:bg-slate-900"
        style={{
          animation: 'vehicleEditorIn 0.2s ease-out'
        }}
      >
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
          .vehicle-editor-page { font-family: 'Inter', system-ui, sans-serif; }
          .vehicle-editor-page .font-mono { font-family: inherit; font-variant-numeric: tabular-nums; }
          .vehicle-editor-shell {
            --ink:#12161B; --ink-soft:#2A313B;
            --paper:#F3F4F7; --surface:#FFFFFF;
            --border:#E4E6EC; --border-soft:#ECEDF2;
            --text:#1B2027; --muted:#6B7280; --faint:#9CA3AF;
            --accent:#F97316; --accent-soft:#FFF7ED; --accent-ink:#EA580C;
            --cold:#1E7FAE; --cold-soft:#E4F2F8;
            --danger:#C43D3D; --danger-soft:#FBE7E7;
            --success:#2F9E6B; --success-soft:#E5F6EE;
            --slate:#5B6472; --slate-soft:#EEF0F3;
          }
          .dark .vehicle-editor-shell {
            --paper: #1e293b;
            --surface: #0f172a;
            --border: #334155;
            --border-soft: #1e293b;
            --text: #f8fafc;
            --muted: #94a3b8;
            --faint: #64748b;
            --cold-soft: rgba(30, 127, 174, 0.25);
            --danger-soft: rgba(196, 61, 61, 0.25);
          }
          @keyframes vehicleEditorIn {
            from { opacity: 0; transform: translateY(8px); }
            to { opacity: 1; transform: translateY(0); }
          }
          @media (prefers-reduced-motion: reduce) { .vehicle-editor-shell { animation: none !important; } }
          .vehicle-editor-shell :is(button, input, select, textarea, [tabindex="0"]):focus-visible { outline-color: #ea580c; }
        `}</style>
        {/* Header avec accent orange */}
        <h1 id={titleId} className="sr-only">{vehicle ? `${readOnly ? 'Consulter' : 'Modifier'} le véhicule ${getVehicleDisplayName(vehicle)}` : 'Nouveau véhicule'}</h1>
        <div className="relative z-50 flex shrink-0 items-center justify-between gap-3 border-b border-slate-100 bg-white px-3 py-3 dark:border-slate-800 dark:bg-slate-900 sm:px-6 sm:py-4">
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <button type="button" onClick={requestClose} disabled={isLoading} aria-label="Retour à la liste des véhicules" title="Retour à la liste des véhicules"
              className="inline-flex min-h-10 shrink-0 items-center gap-1 rounded-lg px-2 text-xs font-semibold text-slate-600 transition-colors hover:bg-orange-50 hover:text-orange-700 disabled:opacity-50 dark:text-slate-300 dark:hover:bg-slate-800">
              <ChevronLeftIcon aria-hidden="true" className="h-4 w-4" /><span className="hidden sm:inline">Liste des véhicules</span><span className="sm:hidden">Liste</span>
            </button>
            <div className="min-w-0">
              <p className="mb-1 text-[10px] font-medium text-slate-500">Flotte / <span className="text-orange-700 dark:text-orange-400">{readOnly ? 'Consultation' : vehicle ? 'Configuration' : 'Nouvel équipement'}</span></p>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <p title={formData.registration_number || 'Sans immatriculation'} className="max-w-full break-all text-base font-semibold tracking-tight text-slate-900 dark:text-white sm:text-xl">
                  {formData.registration_number || (vehicle ? 'Équipement sans immatriculation' : 'Nouvel équipement')}
                </p>
                {formData.vehicle_subtype && (
                  <span className="hidden rounded-full bg-slate-100 px-2.5 py-1 text-[10px] font-medium text-slate-500 dark:bg-slate-800 dark:text-slate-300 sm:inline-flex">
                    {getVehicleSubtypeLabel(formData.vehicle_subtype)}
                  </span>
                )}
                {isLoading && (
                  <span className="animate-spin h-3.5 w-3.5 border-2 border-orange-400 border-t-transparent rounded-full" role="status" aria-label="Sauvegarde en cours" />
                )}
              </div>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2 sm:gap-5">
            <VehicleConfigurationProgress {...sectionProgress} />

          </div>
        </div>
        <div role="status" tabIndex={0} className="vehicle-editor-notice flex shrink-0 flex-wrap items-center justify-between gap-x-4 gap-y-1 overflow-y-auto border-b border-slate-100 bg-white px-3 py-1.5 text-[11px] text-slate-500 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-400 sm:px-6"><span className="inline-flex items-center gap-1.5"><span aria-hidden="true" className={`h-1.5 w-1.5 shrink-0 rounded-full ${isDirty ? 'bg-orange-400' : 'bg-slate-300'}`} />{readOnly ? 'Consultation seule · aucune modification ne sera enregistrée.' : isLoading ? 'Sauvegarde en cours…' : isDirty ? 'Modifications non enregistrées' : 'Aucune modification en attente'}</span><span className="hidden text-[10px] text-slate-400 md:inline">La présence des informations ne constitue pas une validation technique.</span></div>
        {initialPreset && selectedPresetId === initialPreset.id && <div tabIndex={0} className="vehicle-editor-notice max-h-[16dvh] shrink-0 overflow-y-auto border-b border-orange-100 bg-orange-50 px-3 py-2 text-sm text-orange-800 dark:border-orange-500/20 dark:bg-orange-500/10 dark:text-orange-300 sm:px-6"><strong>{initialPreset.label}</strong> · Modèle chargé. {registrationRequired ? 'Renseignez l’immatriculation et vérifiez les valeurs indicatives avant de sauvegarder.' : 'Vérifiez les valeurs indicatives, puis sauvegardez. L’immatriculation pourra être ajoutée plus tard.'}</div>}
        {profileUndo && !readOnly && <div className="shrink-0 bg-orange-50 px-3 py-1 text-xs text-orange-900 dark:bg-orange-950 dark:text-orange-200"><button type="button" disabled={isLoading} title="Restaurer la configuration précédant ce changement" onClick={() => { setFormData(profileUndo.data); setSelectedPresetId(profileUndo.presetId); setProfileUndo(null) }} className="min-h-8 font-semibold underline">Annuler ce changement de profil</button></div>}
        {(formError || saveError) && <div id={`${titleId}-error`} role="alert" tabIndex={0} className="vehicle-editor-notice max-h-[16dvh] shrink-0 overflow-y-auto bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-950/40 dark:text-red-300 sm:px-6">{formError || saveError}</div>}
        <form ref={formRef} id="vehicle-form" onSubmit={handleSubmit} onInvalidCapture={event => {
          if (!(event.target as HTMLElement).hasAttribute('data-vehicle-registration')) return
          event.preventDefault()
          setFormError('Renseignez une immatriculation ou une référence de 1 à 50 caractères.')
          setRegistrationFocusRequest(request => request + 1)
        }} className="relative min-h-0 flex-1 overflow-hidden">
          <fieldset disabled={isLoading} className="m-0 h-full min-w-0 border-0 p-0">
          <legend className="sr-only">Configuration du véhicule</legend>
          <VehicleConfigRail
            onCompletionChange={setSectionProgress}
            context="fleet"
            initialPanel="axles"
            mode={readOnly ? 'view' : 'edit'}
            registrationRequired={registrationRequired}
            registrationFocusRequest={registrationFocusRequest}
            registrationErrorId={formError ? `${titleId}-error` : undefined}
            isTractor={isTractorVehicle}
            height="100%"
            vehicle={formData as any}
            onChange={(updated) => {
              if (isLoading || readOnly) return
              setProfileUndo(null)
              if (updated.registration_number !== formData.registration_number) setFormError('')
              setFormData(prev => ({
                ...prev,
                ...updated,
              } as Partial<Vehicle>))
            }}
            selectedPresetId={selectedPresetId}
            onRequestProfile={readOnly ? undefined : requestProfileChange}
            profileTriggerRef={profileTriggerRef}
            allowCustomType={!vehicle && !selectedPresetId && !formData.axle_configuration && ![formData.length, formData.width, formData.height, formData.max_weight_permitted, formData.empty_weight].some(value => optionalVehicleNumber(value) != null)}
          />
          </fieldset>
        </form>

        <div className="relative z-50 flex shrink-0 items-center justify-between gap-2 border-t border-slate-100 bg-white px-3 py-2 dark:border-slate-800 dark:bg-slate-900 sm:px-6 sm:py-3" style={{ paddingBottom: 'max(0.5rem, env(safe-area-inset-bottom))' }}>
          <button
            type="button"
            onClick={requestClose}
            disabled={isLoading}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2.5 text-sm font-semibold text-slate-600 transition-all hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 sm:px-5"
          >
            <XMarkIcon className="w-4 h-4" />
            {readOnly ? 'Fermer' : 'Annuler'}
          </button>
          {!readOnly && <button
            type="submit"
            form="vehicle-form"
            disabled={isLoading}
            className="inline-flex items-center gap-2 rounded-xl bg-orange-500 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-60 sm:px-6"
          >
            {isLoading ? (
              <>
                <div className="animate-spin h-4 w-4 border-2 border-white/50 border-t-white rounded-full" />
                Sauvegarde…
              </>
            ) : (
              <>
                <ArrowDownTrayIcon className="w-4 h-4" />
                Sauvegarder
              </>
            )}
          </button>}
        </div>
      </div>
    {discardRequested && <VehicleActionDialog title="Abandonner les modifications ?" confirmLabel="Abandonner et fermer" onClose={() => setDiscardRequested(false)} onConfirm={onClose}><p>Vos modifications non enregistrées seront perdues. Revenez à l’édition pour les conserver et les sauvegarder.</p></VehicleActionDialog>}
    {profilePickerOpen && !readOnly && <VehicleProfileDialog vehicle={formData} selectedPresetId={selectedPresetId} returnFocus={profileTriggerRef}
      onClose={() => setProfilePickerOpen(false)} onApply={(preset, next) => {
        if (isLoading || readOnly) return
        setProfileUndo({ data: structuredClone(formData), presetId: selectedPresetId })
        setFormData(next)
        setSelectedPresetId(preset.id)
        setProfilePickerOpen(false)
      }} />}
    </section>
  )
}
